import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Aperture, Flame, Loader2, Reply } from "lucide-react";
import { SnapDialog } from "@/components/aph/SnapDialog";
import { useVault } from "@/context/VaultContext";
import { setSecureScreen, onScreenshot } from "@/lib/native";
import { toast } from "sonner";

function useSnapUrl(m) {
  const { getFileBytes } = useVault();
  const [url, setUrl] = useState(null);
  const local = m.content.snap?.local;
  useEffect(() => {
    if (!local) return;
    let u;
    getFileBytes(m.message_id).then((b) => { if (b) { u = URL.createObjectURL(new Blob([b], { type: m.content.snap.mime })); setUrl(u); } });
    return () => u && URL.revokeObjectURL(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, m.message_id]);
  return url;
}

function Viewer({ url, m }) {
  const { reportScreenshot } = useVault();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setSecureScreen(true);
    const off = onScreenshot(() => { reportScreenshot(m.message_id); toast("Screenshot detected — the sender was notified"); });
    return () => { off(); setSecureScreen(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.message_id]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 100); return () => clearInterval(t); }, []);
  const left = m.burn_at ? Math.max(0, m.burn_at - now) : m.timer * 1000;
  const pct = left / (m.timer * 1000);
  return (
    <div className="fixed inset-0 z-[60] bg-void flex items-center justify-center select-none" data-testid={`snap-viewer-${m.message_id}`} onContextMenu={(e) => e.preventDefault()}>
      <img src={url} alt="" draggable={false} className="max-h-full max-w-full object-contain pointer-events-none" />
      <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-4 flex items-center gap-2 bg-void/70 border border-flare/40 rounded-full pl-2 pr-3 py-1 text-flare font-mono text-sm">
        <svg viewBox="0 0 20 20" className="w-5 h-5 -rotate-90"><circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,91,91,0.2)" strokeWidth="2" /><circle cx="10" cy="10" r="8" fill="none" stroke="#FF5B5B" strokeWidth="2" strokeDasharray={50.3} strokeDashoffset={50.3 * (1 - pct)} /></svg>
        {Math.ceil(left / 1000)}s
      </div>
      <p className="absolute bottom-[max(1.5rem,env(safe-area-inset-bottom))] text-xs text-ash/40">Release to hide · burns when the timer ends</p>
    </div>
  );
}

export function SnapBubble({ m, mine, senderName }) {
  const { openSnap } = useVault();
  const [holding, setHolding] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const url = useSnapUrl(m);
  const opened = m.status === "destroyed";

  useEffect(() => {
    if (!holding) return;
    const up = () => setHolding(false);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => { window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); };
  }, [holding]);

  const start = (e) => {
    e.preventDefault();
    if (!url || opened) return;
    openSnap(m.message_id);
    setHolding(true);
  };

  let label;
  let tone = "text-stellar border-stellar/40";
  if (opened) { label = "Snap opened"; tone = "text-flare/70 border-flare/25 border-dashed"; }
  else if (mine) label = m.read_by.length ? "Opened" : m.delivered_to.length ? "Delivered" : "Sent";
  else if (m.content.snap.failed) { label = "Snap expired"; tone = "text-flare/70 border-flare/25"; }
  else if (!url) label = "Decrypting…";
  else label = m.status === "burning" ? "Hold to keep viewing" : `Hold to view · ${m.timer}s`;

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} rise-in`} data-testid={`message-${m.message_id}`} data-state={m.status}>
      <div className="max-w-[78%]">
        {senderName && !mine && <p className="text-[11px] font-branding uppercase tracking-wider text-stellar/80 mb-1">{senderName}</p>}
        <button onPointerDown={start} disabled={mine || opened || !url} data-testid={`snap-open-btn-${m.message_id}`} style={{ touchAction: "none" }}
          className={`flex items-center gap-3 px-4 py-3 rounded-secure border bg-nebula transition-shadow ${tone} ${!mine && url && !opened ? "hover:shadow-stellar-glow cursor-pointer" : "cursor-default"}`}>
          {opened ? <Flame size={18} strokeWidth={1.5} /> : !url && !mine && !m.content.snap.failed ? <Loader2 size={18} className="animate-spin" /> : <Aperture size={18} strokeWidth={1.5} />}
          <span className="text-sm font-branding">{mine && !opened ? (m.content.snap.reply ? "Snap reply" : "Snap") : label}</span>
          {!mine && !opened && m.content.snap.reply && <span className="text-[10px] text-ash/40">reply</span>}
          {mine && !opened && <span className="text-[11px] text-ash/45" data-testid={`snap-status-${m.message_id}`}>{label} · {m.timer}s</span>}
        </button>
      </div>
      {!mine && (
        <button onClick={() => setReplyOpen(true)} data-testid={`snap-reply-btn-${m.message_id}`} className="self-end ml-2 mb-1 p-2 rounded-secure border border-rim text-ash/55 hover:text-stellar hover:border-stellar/40 transition-colors" title="Reply with a Snap">
          <Reply size={16} strokeWidth={1.5} />
        </button>
      )}
      <SnapDialog open={replyOpen} onOpenChange={setReplyOpen} sid={m.session_id} reply />
      {holding && url && !opened && createPortal(<Viewer url={url} m={m} />, document.body)}
    </div>
  );
}
